import { apiClient } from './client';
import type { Task, CreateTaskDto, UpdateTaskDto } from '@/types/task.types';

export class TaskService {
  async getEventTasks(eventId: number, cacheBust = false): Promise<Task[]> {
    const url = cacheBust 
      ? `/events/${eventId}/tasks?_t=${Date.now()}`
      : `/events/${eventId}/tasks`;
    const response = await apiClient.get(url);
    return response.data.data || [];
  }

  async getCelebrationTasks(celebrationId: number, cacheBust = false): Promise<Task[]> {
    const url = cacheBust 
      ? `/celebrations/${celebrationId}/tasks?_t=${Date.now()}`
      : `/celebrations/${celebrationId}/tasks`;
    const response = await apiClient.get(url);
    return response.data.data || [];
  }

  async createTask(eventId: number, taskData: CreateTaskDto): Promise<Task> {
    const response = await apiClient.post(`/events/${eventId}/tasks`, taskData);
    return response.data.data;
  }

  async createCelebrationTask(celebrationId: number, taskData: CreateTaskDto): Promise<Task> {
    const response = await apiClient.post(`/celebrations/${celebrationId}/tasks`, taskData);
    return response.data.data;
  }

  async updateTask(eventId: number, taskId: number, taskData: UpdateTaskDto): Promise<Task> {
    if (!taskId || typeof taskId !== 'number' || isNaN(taskId)) {
      throw new Error(`updateTask called with invalid taskId: ${JSON.stringify(taskId)}`);
    }
    const response = await apiClient.put(`/events/${eventId}/tasks/${taskId}`, taskData);
    return response.data.data;
  }

  async updateCelebrationTask(celebrationId: number, taskId: number, taskData: UpdateTaskDto): Promise<Task> {
    if (!taskId || typeof taskId !== 'number' || isNaN(taskId)) {
      throw new Error(`updateCelebrationTask called with invalid taskId: ${JSON.stringify(taskId)}`);
    }
    const response = await apiClient.put(`/celebrations/${celebrationId}/tasks/${taskId}`, taskData);
    return response.data.data;
  }

  async deleteTask(eventId: number, taskId: number): Promise<void> {
    await apiClient.delete(`/events/${eventId}/tasks/${taskId}`);
  }

  async deleteCelebrationTask(celebrationId: number, taskId: number): Promise<void> {
    await apiClient.delete(`/celebrations/${celebrationId}/tasks/${taskId}`);
  }

  async assignTask(eventId: number, taskId: number, userId: number): Promise<Task> {
    const response = await apiClient.put(`/events/${eventId}/tasks/${taskId}/assign`, { assigned_to: userId });
    return response.data.data;
  }

  async getUserPendingTasks(limit?: number): Promise<Task[]> {
    const params = limit ? { status: 'pending', limit } : { status: 'pending' };
    const response = await apiClient.get('/tasks/my-tasks', { params });
    return response.data.data || [];
  }
}

export const taskService = new TaskService();
